import React, { useState } from 'react'
import { Box, Typography, styled } from '@mui/material'
import { Auth } from 'aws-amplify'
import toast from 'react-hot-toast'
import { Button, FormGroup, Input } from '@/design-system'
import { useAuth } from '@/hooks/common'
import { Patterns } from '@/utils/helper'
import { SettingsField, fieldInputSx } from '../SettingsField'
import { Toggle } from '../Toggle'

const PanelHead = styled(Box)({ marginBottom: 18 })
const H2 = styled(Typography)({ fontSize: 20, fontWeight: 720, letterSpacing: '-0.025em', color: 'var(--ds-ink)' })
const Sub = styled(Typography)({ fontSize: 13, color: 'var(--ds-ink-subtle)', marginTop: 4 })
const GroupTitle = styled('h3')({ fontSize: 15, fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ds-ink)', margin: '0 0 20px' })

const SetRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '16px 0',
  borderTop: '1px solid var(--ds-border)',
  '&:first-of-type': { borderTop: 'none', paddingTop: 0 },
  '&:last-of-type': { paddingBottom: 0 },
})

const Lft = styled(Box)({ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 })
const RowIc = styled(Box)({ width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 })
const RowT = styled('div')({ fontSize: 13.5, fontWeight: 640, color: 'var(--ds-ink)' })
const RowS = styled('div')({ fontSize: 12, color: 'var(--ds-ink-subtle)', marginTop: 2 })

const Tag = styled('span')({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.09em',
  padding: '3px 8px',
  borderRadius: 'var(--ds-radius-pill)',
  fontWeight: 600,
  flexShrink: 0,
})

function detectDevice() {
  const ua = navigator.userAgent
  const os = ua.includes('Mac') ? 'macOS' : ua.includes('Windows') ? 'Windows' : ua.includes('Linux') ? 'Linux' : 'Unknown OS'
  const browser = ua.includes('Chrome') ? 'Chrome' : ua.includes('Firefox') ? 'Firefox' : ua.includes('Safari') ? 'Safari' : 'Browser'
  return `${os} · ${browser}`
}

/** Password (real Cognito call), a labeled-not-faked 2FA placeholder, and the one session we can actually see. */
export function AccountSecuritySection() {
  const { logout } = useAuth()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [saving, setSaving] = useState(false)

  const mismatch = confirm.length > 0 && confirm !== next
  const weak = next.length > 0 && !Patterns.password.test(next)
  const canSubmit = current.length > 0 && next.length > 0 && confirm.length > 0 && !mismatch && !weak

  const handleUpdatePassword = async () => {
    if (!canSubmit || saving) return
    setSaving(true)
    try {
      const user = await Auth.currentAuthenticatedUser()
      await toast.promise(Auth.changePassword(user, current, next), {
        loading: 'Changing password…',
        success: 'Password updated',
        error: 'Wrong current password or failed to update',
      })
      setCurrent('')
      setNext('')
      setConfirm('')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not change password')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box>
      <PanelHead>
        <H2>Account &amp; Security</H2>
        <Sub>Password, two-factor, and where you&rsquo;re signed in.</Sub>
      </PanelHead>

      <FormGroup>
        <GroupTitle>Change password</GroupTitle>
        <SettingsField label='Current password'>
          <Input variant='filled' fullWidth sx={fieldInputSx} type='password' value={current} onChange={(e) => setCurrent(e.target.value)} placeholder='••••••••' />
        </SettingsField>
        <SettingsField label='New password'>
          <Input variant='filled' fullWidth sx={fieldInputSx} type='password' value={next} onChange={(e) => setNext(e.target.value)} placeholder='••••••••' />
          {weak && <Box sx={{ fontSize: 11.5, color: 'var(--ds-danger)', marginTop: 0.75 }}>8+ characters, upper &amp; lower case, a number and a symbol.</Box>}
        </SettingsField>
        <SettingsField label='Confirm new password'>
          <Input variant='filled' fullWidth sx={fieldInputSx} type='password' value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder='••••••••' />
          {mismatch && <Box sx={{ fontSize: 11.5, color: 'var(--ds-danger)', marginTop: 0.75 }}>Passwords don&rsquo;t match.</Box>}
        </SettingsField>
        <Box sx={{ marginTop: 0.5 }}>
          <Button variant='primary' size='sm' disabled={!canSubmit} loading={saving} onClick={handleUpdatePassword}>
            Update password
          </Button>
        </Box>
      </FormGroup>

      <FormGroup label='Security'>
        <SetRow>
          <Lft>
            <RowIc sx={{ background: 'rgba(1,218,254,0.14)', color: '#0891a8' }}>🛡</RowIc>
            <Box>
              <RowT>Two-factor authentication</RowT>
              <RowS>Add an extra layer of security to sign-in</RowS>
            </Box>
          </Lft>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tag sx={{ background: 'var(--ds-hover)', color: 'var(--ds-ink-subtle)' }}>SOON</Tag>
            <Toggle checked={false} disabled aria-label='Two-factor authentication — coming soon' />
          </Box>
        </SetRow>
      </FormGroup>

      <FormGroup label='This session'>
        <SetRow>
          <Lft>
            <RowIc sx={{ background: 'rgba(190,157,253,0.14)', color: 'var(--ds-accent-deep-b)' }}>💻</RowIc>
            <Box>
              <RowT>{detectDevice()}</RowT>
              <RowS>This device</RowS>
            </Box>
          </Lft>
          <Tag sx={{ background: 'rgba(1,218,254,0.14)', color: '#0891a8' }}>THIS DEVICE</Tag>
        </SetRow>
        <SetRow>
          <Lft>
            <RowIc sx={{ background: 'color-mix(in srgb, var(--ds-accent-deep-a) 12%, transparent)', color: 'var(--ds-accent-deep-a)' }}>⏻</RowIc>
            <Box>
              <RowT>Sign out</RowT>
              <RowS>Sign out of your account on this device</RowS>
            </Box>
          </Lft>
          <Button variant='secondary' size='sm' onClick={logout}>
            Sign out
          </Button>
        </SetRow>
      </FormGroup>
    </Box>
  )
}
